import React from 'react';
import { Button, Card, CardContent, CardHeader, Typography, Box } from '@mui/material';

interface TemplateVariablePickerProps {
  onVariableInsert: (variable: string) => void;
}

interface Variable {
  name: string;
  placeholder: string;
  description: string;
  usedIn: string[];
}

const AVAILABLE_VARIABLES: Variable[] = [
  {
    name: 'var7',
    placeholder: '{#var7#}',
    description: 'Customer Name',
    usedIn: ['notice_only'],
  },
  {
    name: 'var',
    placeholder: '{#var#}',
    description: 'Policy Number',
    usedIn: ['notice_only'],
  },
  {
    name: 'var1',
    placeholder: '{#var1#}',
    description: 'Policy Number',
    usedIn: ['notice_with_link'],
  },
  {
    name: 'var2',
    placeholder: '{#var2#}',
    description: 'Vehicle Registration Number',
    usedIn: ['MOTOR'],
  },
  {
    name: 'var3',
    placeholder: '{#var3#}',
    description: 'Expiry Date (DD-MM-YYYY)',
    usedIn: ['notice_with_link'],
  },
  {
    name: '_var4',
    placeholder: '{#_var4#}',
    description: 'PDF Link',
    usedIn: ['notice_only'],
  },
  {
    name: 'var4',
    placeholder: '{#var4#}',
    description: 'PDF Link',
    usedIn: ['notice_with_link'],
  },
  {
    name: 'var5',
    placeholder: '{#var5#}',
    description: 'Bitly Link (Renewal Journey)',
    usedIn: ['notice_with_link'],
  },
  {
    name: 'var6',
    placeholder: '{#var6#}',
    description: 'Product Name',
    usedIn: ['HEALTH'],
  },
];

export const TemplateVariablePicker: React.FC<TemplateVariablePickerProps> = ({
  onVariableInsert,
}) => {
  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardHeader 
        sx={{ 
          paddingBottom: '12px', 
          backgroundColor: 'rgba(249, 250, 251, 0.5)', 
          borderBottom: '1px solid #e5e7eb',
          flexShrink: 0
        }}
      >
        <Typography variant="h6" sx={{ fontSize: '14px', fontWeight: 500 }}>
          Available Variables
        </Typography>
      </CardHeader>
      <CardContent sx={{ padding: '16px', overflow: 'auto', flex: 1 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography variant="body2" sx={{ fontSize: '12px', color: '#6b7280', marginBottom: '12px' }}>
            Click a variable to insert it into your template
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {AVAILABLE_VARIABLES.map((variable) => (
              <Button
                key={variable.name}
                variant="outlined"
                size="small"
                onClick={() => onVariableInsert(variable.placeholder)}
                sx={{
                  justifyContent: 'flex-start',
                  textAlign: 'left',
                  height: 'auto',
                  padding: '8px',
                  textTransform: 'none',
                }}
              >
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', width: '100%' }}>
                  <Box
                    component="code"
                    sx={{
                      fontSize: '12px',
                      fontFamily: 'monospace',
                      backgroundColor: '#f3f4f6',
                      padding: '2px 4px',
                      borderRadius: '4px'
                    }}
                  >
                    {variable.placeholder}
                  </Box>
                  <Typography variant="body2" sx={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                    {variable.description}
                  </Typography>
                  {variable.usedIn.length > 0 && (
                    <Typography variant="body2" sx={{ fontSize: '12px', color: '#2563eb', marginTop: '2px' }}>
                      Used in: {variable.usedIn.join(', ')}
                    </Typography>
                  )}
                </Box>
              </Button>
            ))}
          </Box>
          
          <Box
            sx={{
              marginTop: '16px',
              padding: '12px',
              backgroundColor: '#fffbeb',
              border: '1px solid #fde68a',
              borderRadius: '6px'
            }}
          >
            <Typography variant="body2" sx={{ fontSize: '12px', color: '#92400e', fontWeight: 500, marginBottom: '4px' }}>
              Variable Guidelines:
            </Typography>
            <Box component="ul" sx={{ fontSize: '12px', color: '#78350f', margin: 0, paddingLeft: '20px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <li>
                Use <Box component="code" sx={{ backgroundColor: '#fff', padding: '2px 4px' }}>var</Box> and{' '}
                <Box component="code" sx={{ backgroundColor: '#fff', padding: '2px 4px' }}>_var4</Box> for notice_only templates
              </li>
              <li>
                Use <Box component="code" sx={{ backgroundColor: '#fff', padding: '2px 4px' }}>var1-var6</Box> for notice_with_link templates
              </li>
              <li>
                <Box component="code" sx={{ backgroundColor: '#fff', padding: '2px 4px' }}>var2</Box> is specific to MOTOR policies
              </li>
              <li>
                <Box component="code" sx={{ backgroundColor: '#fff', padding: '2px 4px' }}>var6</Box> is specific to HEALTH policies
              </li>
            </Box>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};
